import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import { getRentalPortalApplicant } from '../../../api'
import {
  getPageBrowserTabTitleWithEntityName,
  usePageBrowserTabTitle,
  useUserData,
} from '../../../hooks'
import { EmailDraftProvider } from '../../Email'
import { useAsyncEntity } from '../../Entity'
import { EntityPageProfileLayout } from '../../Page'
import {
  getIsMockApplicantId,
  getMockApplicantDetail,
  toApplicantDetail,
} from '../../RentalPortalApplicant'
import { RentalPortalProfilePreviewOpenButton } from '../RentalPortalProfilePreviewOpenButton'

import {
  QA_NAME_APPLICANT_PROFILE_PAGE,
  QA_NAME_APPLICANT_PROFILE_PREVIEW_OPEN_PROFILE,
} from './RentalPortalApplicantProfile.constants'
import { ApplicantProfileContext } from './RentalPortalApplicantProfile.context'
import {
  getApplicantProfilePreviewOpenFullPageProps,
  getApplicantProfileTitle,
  getIsApplicantProfileNotFound,
} from './RentalPortalApplicantProfile.utils'
import { APPLICANT_PROFILE_EMAILS_DRAFT_PREFIX } from './RentalPortalApplicantProfileEmails'
import { RentalPortalApplicantProfileHeader } from './RentalPortalApplicantProfileHeader'
import { useApplicantProfileNoteEdit } from './RentalPortalApplicantProfileHeaderContent'
import { RentalPortalApplicantProfileLoadState } from './RentalPortalApplicantProfileLoadState'
import {
  RentalPortalApplicantProfileTabOverview,
  useApplicantDecisionActions,
} from './RentalPortalApplicantProfileTabOverview'
import { RentalPortalApplicantProfileTabs } from './RentalPortalApplicantProfileTabs'
import { RentalPortalApplicantProfileWidgetList } from './RentalPortalApplicantProfileWidgetList'

function RentalPortalApplicantProfile({
  applicantId: applicantIdProp,
  isPreview,
  layoutProps,
  onClosePreview,
}) {
  const { applicantId: applicantIdFromParams } = useParams()
  const { t } = useTranslation()

  const currentUser = useUserData()

  const [modal, setModal] = useState(null)

  const applicantId = applicantIdProp ?? applicantIdFromParams

  async function getProfileData() {
    return getIsMockApplicantId(applicantId)
      ? getMockApplicantDetail({ applicantId, currentUser })
      : getRentalPortalApplicant(applicantId).then(toApplicantDetail)
  }

  const {
    entityData,
    handleEntityInit,
    handleEntityReload,
    handleEntitySilentReload,
    isInitiated,
    isLoading,
    serverError,
  } = useAsyncEntity({ entityId: applicantId, getProfileData })

  function handleCloseModal() {
    setModal(null)
  }

  const decisionActions = useApplicantDecisionActions({
    applicantData: entityData,
    applicantId,
    handleCloseModal,
    handleEntityReload,
    setModal,
  })

  const noteEdit = useApplicantProfileNoteEdit({
    applicantId,
    handleEntityReload,
    value: entityData?.content,
  })

  usePageBrowserTabTitle({
    isEnabled: !isPreview,
    isLoading: !isInitiated,
    pageTitle: getPageBrowserTabTitleWithEntityName({
      name: getApplicantProfileTitle({ applicantData: entityData, t }),
      title: t('RentalPortal.ApplicantProfileBrowserTabTitleKey'),
    }),
  })

  function handleClickRetry() {
    handleEntityInit()
  }

  const openFullPageActionsProps =
    isPreview && applicantId
      ? getApplicantProfilePreviewOpenFullPageProps({
          Actions: null,
          applicantId,
          t,
          onClosePreview,
        })
      : null

  return isPreview && serverError ? (
    <div className={layoutProps?.className}>
      <RentalPortalApplicantProfileLoadState
        isNotFound={getIsApplicantProfileNotFound(serverError)}
        previewActionsProps={openFullPageActionsProps}
        onClickRetry={handleClickRetry}
      />
    </div>
  ) : (
    <ApplicantProfileContext
      value={{
        applicantData: entityData,
        applicantId,
        decisionActions,
        handleCloseModal,
        handleEntityReload,
        handleEntitySilentReload,
        isInitiated,
        isLoading,
        isPreview,
        noteEdit,
        setModal,
      }}
    >
      <EmailDraftProvider
        prefix={`${APPLICANT_PROFILE_EMAILS_DRAFT_PREFIX}-${applicantId}`}
      >
        <EntityPageProfileLayout
          {...layoutProps}
          Footer={isPreview ? null : undefined}
          Header={serverError ? null : RentalPortalApplicantProfileHeader}
          headerProps={{ isPreview, openFullPageActionsProps }}
          qaName={QA_NAME_APPLICANT_PROFILE_PAGE}
          widgets={
            serverError || isPreview ? null : (
              <RentalPortalApplicantProfileWidgetList />
            )
          }
        >
          {serverError ? (
            <RentalPortalApplicantProfileLoadState
              isNotFound={getIsApplicantProfileNotFound(serverError)}
              onClickRetry={handleClickRetry}
            />
          ) : isPreview ? (
            <>
              <RentalPortalApplicantProfileTabOverview />
              <RentalPortalProfilePreviewOpenButton
                link={openFullPageActionsProps?.getProfileLink({
                  id: applicantId,
                })}
                qaName={QA_NAME_APPLICANT_PROFILE_PREVIEW_OPEN_PROFILE}
              />
            </>
          ) : (
            <RentalPortalApplicantProfileTabs />
          )}
          {modal}
        </EntityPageProfileLayout>
      </EmailDraftProvider>
    </ApplicantProfileContext>
  )
}

export default RentalPortalApplicantProfile
